import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";

// Import ScrollToTop component
import ScrollToTop from "./components/ScrollToTop";

// Import components
import Navbar from "./components/Navbar";
import HomeNavbar from "./components/HomeNavbar";
import Footer from "./components/Footer";
import AboutMe from "./components/AboutMe";
import Services from "./components/Services";
import ContactPage from "./components/ContactPage";
import Home from "./components/Home";
import Portfolio from "./components/Portfolio";
import AlbumDetails from "./components/AlbumDetails";
import WorksPage from "./components/WorksPage";
import AdminLogin from "./components/AdminLogin";
import Dashboard from "./components/Dashboard";

import "./App.css";

// Main App Layout Component - for non-admin routes
function MainAppLayout({ cartItems, handleAddToCart }) {
  const location = useLocation();
  
  // Check if current route should show footer
  const shouldShowFooter = () => {
    const path = location.pathname;
    
    // Hide footer on these pages:
    const noFooterPages = [
      "/",                // Home page
      "/admin",          // Admin login
      "/dashboard"       // Admin dashboard
    ];
    
    // Check if current path is in noFooterPages
    return !noFooterPages.includes(path) && 
           !path.startsWith("/admin") && 
           !path.startsWith("/dashboard");
  };
  
  // Check if current route is admin route
  const isAdminRoute = location.pathname.startsWith("/admin") || 
                       location.pathname.startsWith("/dashboard");

  return (
    <div className="App">
      {/* Scroll management */}
      <ScrollToTop />
      
      {/* Home page navbar - only for main site, not admin routes */}
      {!isAdminRoute && (
        location.pathname === "/" ? (
          <HomeNavbar />
        ) : (
          <Navbar cartCount={cartItems.length} />
        )
      )}

      {/* Main content area */}
      <main className="main-content-wrapper">
        <Routes>
          {/* Home */}
          <Route path="/" element={<Home />} />

          {/* Works Page */}
          <Route path="/works" element={<WorksPage />} />

          {/* Portfolio - Albums Page */}
          <Route path="/portfolio" element={<Portfolio />} />

          {/* Album Details Page */}
          <Route path="/album/:albumName" element={<AlbumDetails />} />

          {/* About */}
          <Route path="/about" element={<AboutMe />} />

          {/* Services */}
          <Route path="/services" element={<Services />} />

          {/* Contact */}
          <Route path="/contact" element={<ContactPage />} />

          {/* Footer links - Redirect to appropriate pages */}
          <Route path="/pricing" element={<Navigate to="/services" replace />} />
          <Route path="/services/weddings" element={<Navigate to="/services" replace />} />
          <Route path="/services/portraits" element={<Navigate to="/services" replace />} />
          <Route path="/services/events" element={<Navigate to="/services" replace />} />
          <Route path="/services/commercial" element={<Navigate to="/services" replace />} />
          <Route path="/privacy" element={<h1 className="page-title">Privacy Policy Page</h1>} />
          <Route path="/terms" element={<h1 className="page-title">Terms of Service Page</h1>} />
        </Routes>
      </main>

      {/* Show Footer only on specific pages (NOT on home page) */}
      {shouldShowFooter() && <Footer />}
    </div>
  );
}

// Main App Component
function App() {
  const [cartItems, setCartItems] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Authentication check effect
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('adminToken');
      const loginTime = localStorage.getItem('adminLoginTime');
      
      if (token && loginTime) {
        const currentTime = new Date().getTime();
        const loginTimestamp = parseInt(loginTime);
        const sessionDuration = 30 * 60 * 1000; // 30 minutes in milliseconds
        
        // Check if session expired
        if (currentTime - loginTimestamp > sessionDuration) {
          // Session expired, clear storage
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          localStorage.removeItem('adminLoginTime');
          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(true);
        }
      } else {
        setIsAuthenticated(false);
      }
    };
    
    checkAuth();
    
    // Check authentication periodically
    const interval = setInterval(checkAuth, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, []);

  // Cart handling
  const handleAddToCart = (item) => {
    const existingItem = cartItems.find(cartItem => cartItem.id === item.id);

    if (existingItem) {
      setCartItems(
        cartItems.map(cartItem =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        )
      );
    } else {
      setCartItems([...cartItems, { ...item, quantity: 1 }]);
    }
  };

  return (
    <Router>
      <Routes>
        {/* Admin Routes */}
        <Route 
          path="/admin" 
          element={
            !isAuthenticated ? (
              <AdminLogin setIsAuthenticated={setIsAuthenticated} />
            ) : (
              <Navigate to="/dashboard" replace />
            )
          } 
        />
        
        <Route 
          path="/dashboard" 
          element={
            isAuthenticated ? (
              <Dashboard setIsAuthenticated={setIsAuthenticated} />
            ) : (
              <Navigate to="/admin" replace />
            )
          } 
        />
        
        {/* Main App Routes - All non-admin routes */}
        <Route 
          path="/*" 
          element={
            <MainAppLayout
              cartItems={cartItems}
              handleAddToCart={handleAddToCart}
            />
          } 
        />
      </Routes>
    </Router>
  );
}

export default App;
